import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';

/**
 * areFiltersEqual - Realiza una comparación profunda de filtros para evitar
 * renderizados y llamadas infinitas en cascada entre padres e hijos.
 */
const areFiltersEqual = (filtersA, filtersB) => {
    const cleanA = filtersA || {};
    const cleanB = filtersB || {};
    const keysA = Object.keys(cleanA);
    const keysB = Object.keys(cleanB);
    
    if (keysA.length !== keysB.length) return false;
    
    return keysA.every(key => {
        const valA = cleanA[key];
        const valB = cleanB[key];
        
        if (Array.isArray(valA) && Array.isArray(valB)) {
            if (valA.length !== valB.length) return false;
            return valA.every((item, idx) => item === valB[idx]);
        }
        return valA === valB;
    });
};

/**
 * FiltrosDinamicos - Versión de alta fidelidad con soporte de sincronización de estados predeterminados.
 * 1- Inicia colapsado por defecto.
 * 2- Organizado en grid de 5 columnas (lg:grid-cols-5).
 * 3- Las etiquetas (labels) están dentro de los campos.
 */
const FiltrosDinamicos = ({ config = [], onFilterChange, defaultValues = {}, initialValues = {} }) => {
    
    // Unificamos las propiedades de inicialización del padre de forma segura
    const mergedDefaults = useMemo(() => ({
        ...(defaultValues || {}),
        ...(initialValues || {})
    }), [defaultValues, initialValues]);

    const [values, setValues] = useState(() => mergedDefaults);
    const [openDropdown, setOpenDropdown] = useState(null);
    const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
    const [isExpanded, setIsExpanded] = useState(false); // Inicia cerrado por defecto
    const containerRef = useRef(null);
    const isFirstRender = useRef(true);

    // ◄ SOLUCIÓN SENIOR: Almacenamos el historial de lo que el padre nos inyectó para evitar sobreescritura local
    const prevMergedDefaultsRef = useRef(mergedDefaults);

    // --- SINCRO DE VALORES DEL PADRE ---
    // Si el padre cambia las propiedades del filtro de forma externa,
    // actualizamos nuestro estado local protegiendo de bucles infinitos.
    useEffect(() => {
        if (!areFiltersEqual(prevMergedDefaultsRef.current, mergedDefaults)) {
            setValues(mergedDefaults);
            prevMergedDefaultsRef.current = mergedDefaults;
        }
    }, [mergedDefaults]);

    // Cerrar dropdowns al hacer clic fuera de su contenedor
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (openDropdown && !event.target.closest('.dropdown-container') && !event.target.closest('.dropdown-trigger')) {
                setOpenDropdown(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [openDropdown]);

    // Notificar cambios al padre con debounce de 300ms para no saturar los ordenadores
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        const timer = setTimeout(() => {
            onFilterChange(values || {});
        }, 300);
        return () => clearTimeout(timer);
    }, [values, onFilterChange]);

    const handleTriggerClick = (e, fieldName) => {
        if (openDropdown === fieldName) {
            setOpenDropdown(null);
        } else {
            const rect = e.currentTarget.getBoundingClientRect();
            setDropdownPos({
                top: rect.bottom + window.scrollY,
                left: rect.left + window.scrollX,
                width: Math.max(rect.width, 220)
            });
            setOpenDropdown(fieldName);
        }
    };

    const handleChange = useCallback((name, value) => {
        setValues(prev => ({ ...prev, [name]: value }));
    }, []);

    const toggleMultiSelect = (name, optionValue) => {
        setValues(prev => {
            const currentValues = Array.isArray(prev[name]) ? prev[name] : [];
            const newValues = currentValues.includes(optionValue)
                ? currentValues.filter(v => v !== optionValue)
                : [...currentValues, optionValue];
            return { ...prev, [name]: newValues };
        });
    };

    const handleSelectAll = (name, options) => {
        const allValues = options.map(opt => opt.value);
        handleChange(name, allValues);
    };

    const handleClearField = (name) => {
        handleChange(name, []);
    };

    const handleReset = () => {
        const resetValues = {};
        config.forEach(f => {
            resetValues[f.name] = f.isMulti ? [] : '';
            if (f.type === 'date-range') {
                resetValues[`${f.name}Desde`] = '';
                resetValues[`${f.name}Hasta`] = '';
            }
        });
        setValues(resetValues);
        setOpenDropdown(null);
    };

    return (
        <div className="bg-white border-b border-slate-200 shadow-sm relative z-[90]" ref={containerRef}>
            {/* Header: Barra de control compacta */}
            <div className="px-6 py-2 flex items-center justify-between bg-slate-50/50">
                <div className="flex items-center gap-3">
                    <div className="bg-indigo-600 p-1.5 rounded-lg shadow-sm">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                        </svg>
                    </div>
                    <span className="text-[10px] font-black uppercase text-indigo-900 tracking-widest">Filtros de Búsqueda</span>
                </div>
                
                <div className="flex items-center gap-3">
                    {isExpanded && (
                        <button 
                            onClick={handleReset}
                            className="text-[9px] font-black text-red-500 hover:text-red-700 uppercase tracking-widest px-3 py-1.5 rounded-lg hover:bg-red-50 transition-all border border-transparent hover:border-red-100"
                        >
                            Limpiar Todo
                        </button>
                    )}
                    <button 
                        onClick={() => setIsExpanded(!isExpanded)}
                        className={`flex items-center gap-2 px-4 py-1.5 rounded-xl text-[10px] font-black uppercase transition-all shadow-sm active:scale-95 border ${
                            isExpanded ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                        }`}
                    >
                        {isExpanded ? (
                            <><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 15l7-7 7 7"/></svg> Cerrar Panel</>
                        ) : (
                            <><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7"/></svg> Abrir Filtros</>
                        )}
                    </button>
                </div>
            </div>

            {/* Grid de 5 Columnas con Etiquetas Integradas */}
            {isExpanded && (
                <div className="p-6 pt-4 animate-slide-down bg-white border-t border-slate-100">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                        {config.map((field) => (
                            <div key={field.name} className="flex flex-col">
                                {field.type === 'text' && (
                                    <div className="relative flex items-center h-10 bg-slate-30 border border-slate-200 rounded-xl focus-within:ring-4 focus-within:ring-indigo-500/5 focus-within:border-indigo-400 focus-within:bg-white transition-all shadow-sm px-3">
                                        <span className="text-[11px] font-bold text-indigo-400 uppercase shrink-0 mr-2 border-r border-slate-200 pr-2 leading-none">{field.label}</span>
                                        <input
                                            type="text"
                                            value={values[field.name] ?? ''}
                                            onChange={(e) => handleChange(field.name, e.target.value)}
                                            placeholder="..."
                                            className="h-full w-full text-xs font-bold text-slate-700 placeholder:text-slate-300 outline-none bg-transparent"
                                        />
                                    </div>
                                )}

                                {field.type === 'select' && (
                                    <button
                                        type="button"
                                        onClick={(e) => handleTriggerClick(e, field.name)}
                                        className={`dropdown-trigger h-10 px-3 flex items-center gap-2 text-xs bg-slate-30 border border-slate-200 rounded-xl transition-all shadow-sm hover:bg-slate-100 ${openDropdown === field.name ? 'ring-4 ring-indigo-500/5 border-indigo-400 bg-white' : ''}`}
                                    >
                                        <span className="text-[11px] font-bold text-slate-600 uppercase shrink-0 border-r border-slate-200 pr-2 leading-none whitespace-nowrap">{field.label}</span>
                                        <span className={`truncate font-bold text-left flex-grow ${!values[field.name]?.length ? 'text-slate-400' : 'text-indigo-600'}`}>
                                            {values[field.name]?.length > 0 
                                                ? (values[field.name].length === 1 ? values[field.name][0] : `${values[field.name].length} ítems`) 
                                                : 'Todos'}
                                        </span>
                                        <svg className={`w-3.5 h-3.5 text-slate-300 transition-transform duration-200 ${openDropdown === field.name ? 'rotate-180 text-indigo-500' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M19 9l-7 7-7-7"/></svg>
                                    </button>
                                )}

                                {field.type === 'date-range' && (
                                    <button
                                        type="button"
                                        onClick={(e) => handleTriggerClick(e, field.name)}
                                        className={`dropdown-trigger h-10 px-3 flex items-center gap-2 text-xs bg-slate-30 border border-slate-200 rounded-xl transition-all shadow-sm hover:bg-slate-100 ${openDropdown === field.name ? 'ring-4 ring-indigo-500/5 border-indigo-400 bg-white' : ''}`}
                                    >
                                        <span className="text-[11px] font-bold text-slate-600 uppercase shrink-0 border-r border-slate-200 pr-2 leading-none">{field.label}</span>
                                        <span className={`truncate font-bold text-left flex-grow ${!values[`${field.name}Desde`] && !values[`${field.name}Hasta`] ? 'text-slate-400' : 'text-indigo-600'}`}>
                                            {values[`${field.name}Desde`] || values[`${field.name}Hasta`] ? 'Personalizado' : 'Cualquiera'}
                                        </span>
                                        <svg className="w-4 h-4 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Dropdowns Flotantes (Portales Ligeros) */}
            {openDropdown && (
                <div 
                    className="fixed dropdown-container z-[9999] mt-2 animate-in fade-in slide-in-from-top-1 duration-200"
                    style={{ top: dropdownPos.top, left: dropdownPos.left, minWidth: dropdownPos.width }}
                >
                    <div className="bg-white border border-slate-200 rounded-2xl shadow-2xl py-1.5 overflow-hidden flex flex-col">
                        {config.find(f => f.name === openDropdown)?.type === 'select' && (
                            <>
                                <div className="px-3 py-2 flex items-center gap-2 border-b border-slate-50 mb-1">
                                    <button onClick={() => handleSelectAll(openDropdown, config.find(f => f.name === openDropdown).options)} className="flex-1 py-1.5 text-[9px] font-black uppercase tracking-tighter bg-indigo-50 text-indigo-600 rounded-lg">Todo</button>
                                    <button onClick={() => handleClearField(openDropdown)} className="flex-1 py-1.5 text-[9px] font-black uppercase tracking-tighter bg-slate-50 text-slate-400 rounded-lg">Limpiar</button>
                                </div>
                                <div className="overflow-y-auto custom-scrollbar px-1.5 max-h-[280px]">
                                    {config.find(f => f.name === openDropdown).options?.map((opt) => (
                                        <button
                                            type="button"
                                            key={opt.value}
                                            onClick={() => toggleMultiSelect(openDropdown, opt.value)}
                                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs transition-all mb-0.5 ${
                                                values[openDropdown]?.includes(opt.value) ? 'bg-indigo-50 text-indigo-700 font-bold' : 'hover:bg-slate-50 text-slate-600 font-medium'
                                            }`}
                                        >
                                            <div className={`w-3.5 h-3.5 rounded-md border-2 flex items-center justify-center ${values[openDropdown]?.includes(opt.value) ? 'bg-indigo-600 border-indigo-600' : 'bg-white border-slate-200'}`}>
                                                {values[openDropdown]?.includes(opt.value) && <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>}
                                            </div>
                                            <span className="truncate">{opt.label}</span>
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}
                        {config.find(f => f.name === openDropdown)?.type === 'date-range' && (
                            <div className="p-4 flex flex-col gap-4">
                                <div className="flex flex-col gap-1.5 text-left">
                                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Fecha Desde</label>
                                    <input type="date" value={values[`${openDropdown}Desde`] ?? ''} onChange={(e) => handleChange(`${openDropdown}Desde`, e.target.value)} className="h-10 px-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-indigo-600" />
                                </div>
                                <div className="flex flex-col gap-1.5 text-left">
                                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Fecha Hasta</label>
                                    <input type="date" value={values[`${openDropdown}Hasta`] ?? ''} onChange={(e) => handleChange(`${openDropdown}Hasta`, e.target.value)} className="h-10 px-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-indigo-600" />
                                </div>
                                <button onClick={() => setOpenDropdown(null)} className="mt-2 h-9 w-full bg-indigo-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg shadow-indigo-200 hover:bg-indigo-700">Aplicar Rango</button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <style>{`
                .custom-scrollbar::-webkit-scrollbar { width: 4px; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
                .animate-slide-down { animation: slideDown 0.3s ease-out forwards; }
                @keyframes slideDown { from { opacity: 0; transform: translateY(-5px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
        </div>
    );
};

export default FiltrosDinamicos;